import { useCallback } from 'react'
import { useShallow } from 'zustand/react/shallow'
import {
  useCreateTemplateAttribute,
  useUpdateTemplateAttribute,
  useDeleteTemplateAttribute,
  useBulkCreateTemplateAttributes,
  useBulkDeleteTemplateAttributes,
} from '@/services/queries/template_attributes'
import { listTemplateAttributesQuery } from '@/services/queries/template_attributes'
import deepEqual from 'deep-equal'
import type { TemplateAttribute } from '../api/client'
import type { TimeTravelEvent } from './timeTravelStore'
import { useTimeTravelStore } from './timeTravelStore'

export const useTimeTravel = () => {
  const { mutateAsync: updateTemplateAttribute } = useUpdateTemplateAttribute({
    isTimeTraveling: true,
  })
  const { mutateAsync: createTemplateAttribute } = useCreateTemplateAttribute({
    isTimeTraveling: true,
  })
  const { mutateAsync: deleteTemplateAttribute } = useDeleteTemplateAttribute({
    isTimeTraveling: true,
  })
  const { mutateAsync: bulkCreateTemplateAttributes } =
    useBulkCreateTemplateAttributes({
      isTimeTraveling: true,
    })
  const { mutateAsync: bulkDeleteTemplateAttributes } =
    useBulkDeleteTemplateAttributes({
      isTimeTraveling: true,
    })

  const recordUndoEvent = useTimeTravelStore(useShallow((state) => state.undo))
  const recordRedoEvent = useTimeTravelStore(useShallow((state) => state.redo))
  const pastEvents = useTimeTravelStore((state) => state.pastEvents)
  const futureEvents = useTimeTravelStore((state) => state.futureEvents)

  const undo = useCallback(async () => {
    const pastEvent = pastEvents[pastEvents.length - 1]
    const canBeUndone = pastEvent && pastEvent.source === 'template_attribute'

    if (!canBeUndone) {
      return
    }

    const oldAttribute = pastEvent.old as TemplateAttribute | undefined
    const newAttribute = pastEvent.new as TemplateAttribute | undefined
    const overrides = {} as Partial<TimeTravelEvent>
    const documentPageId = (pastEvent?.new?.document_page ||
      pastEvent?.old?.document_page) as string
    const templateAttributes = await listTemplateAttributesQuery({
      documentPageId,
    })

    // find a template attribute that has the same attributes from the template attributes list
    const targetTemplateAttribute = templateAttributes.find(
      (t) =>
        t.data_type === pastEvent?.new?.data_type &&
        deepEqual(t.template_values, pastEvent?.new?.template_values || {}),
    )

    if (pastEvent.action === 'create' && newAttribute) {
      await deleteTemplateAttribute({
        documentPageId,
        templateAttributeId: targetTemplateAttribute?.id as string,
      })
    } else if (pastEvent.action === 'delete' && oldAttribute) {
      overrides.old = await createTemplateAttribute({
        documentPageId: oldAttribute?.document_page,
        payload: {
          data_type: oldAttribute?.data_type,
          template_values: oldAttribute?.template_values,
        },
      })
    } else if (pastEvent.action === 'update' && oldAttribute) {
      await updateTemplateAttribute({
        templateAttributeId: targetTemplateAttribute?.id as string,
        documentPageId,
        payload: {
          data_type: oldAttribute?.data_type,
          template_values: oldAttribute?.template_values,
        },
      })
    } else if (
      pastEvent.action === 'bulk_delete' &&
      oldAttribute &&
      oldAttribute
    ) {
      // @ts-expect-error bulk actions have multi template attributes
      const oldTemplateAttributes = oldAttribute.templateAttributes as any[]

      const templateAttributesByDocumentPageId = {}
      oldTemplateAttributes.forEach((t) => {
        if (t && t?.document_page) {
          if (t.document_page in templateAttributesByDocumentPageId) {
            templateAttributesByDocumentPageId[t.document_page].push(t)
          } else {
            templateAttributesByDocumentPageId[t.document_page] = [t]
          }
        }
      })

      for (const entry of Object.entries(templateAttributesByDocumentPageId)) {
        const [documentPageId, templateAttributes] = entry

        await bulkCreateTemplateAttributes({
          documentPageId,
          // @ts-expect-error - bulk template attribates differ
          payloads: templateAttributes.map((t) => {
            return {
              data_type: t.data_type,
              created: t.created,
              template_values: t.template_values,
            }
          }),
        })
      }
    }

    recordUndoEvent(overrides)
  }, [
    bulkCreateTemplateAttributes,
    createTemplateAttribute,
    deleteTemplateAttribute,
    pastEvents,
    recordUndoEvent,
    updateTemplateAttribute,
  ])

  const redo = useCallback(async () => {
    const futureEvent = futureEvents[futureEvents.length - 1]
    const canBeRedone =
      futureEvent && futureEvent.source === 'template_attribute'

    if (!canBeRedone) {
      return
    }

    const oldAttribute = futureEvent.old as TemplateAttribute | undefined
    const newAttribute = futureEvent.new as TemplateAttribute | undefined
    const overrides = {} as Partial<TimeTravelEvent>

    const documentPageId = (futureEvent?.new?.document_page ||
      futureEvent?.old?.document_page) as string
    const templateAttributes = await listTemplateAttributesQuery({
      documentPageId,
    })

    // find a template attribute that has the same attributes from the template attributes list
    const targetTemplateAttribute = templateAttributes.find(
      (t) =>
        t.data_type === futureEvent?.old?.data_type &&
        deepEqual(t.template_values, futureEvent?.old?.template_values || {}),
    )

    if (futureEvent.action === 'create' && newAttribute) {
      overrides.new = await createTemplateAttribute({
        documentPageId: newAttribute?.document_page,
        payload: {
          data_type: newAttribute?.data_type,
          template_values: newAttribute?.template_values,
        },
      })
    } else if (futureEvent.action === 'delete' && oldAttribute) {
      await deleteTemplateAttribute({
        documentPageId,
        templateAttributeId: targetTemplateAttribute?.id as string,
      })
    } else if (futureEvent.action === 'update' && newAttribute) {
      await updateTemplateAttribute({
        documentPageId,
        templateAttributeId: targetTemplateAttribute?.id as string,
        payload: {
          data_type: newAttribute?.data_type,
          template_values: newAttribute?.template_values,
        },
      })
    } else if (futureEvent.action === 'bulk_delete' && oldAttribute) {
      // @ts-expect-error bulk actions have multi template attributes
      const oldTemplateAttributes = oldAttribute.templateAttributes as any[]
      const targetTemplateAttributes = oldTemplateAttributes.map((t) =>
        templateAttributes.find(
          (x) =>
            t.data_type === x.data_type &&
            deepEqual(t.template_values, x.template_values || {}),
        ),
      )

      const targetTemplateAttributesByDocumentPageId = {}
      targetTemplateAttributes.forEach((t) => {
        if (t && t?.document_page) {
          if (t.document_page in targetTemplateAttributesByDocumentPageId) {
            targetTemplateAttributesByDocumentPageId[t.document_page].push(t)
          } else {
            targetTemplateAttributesByDocumentPageId[t.document_page] = [t]
          }
        }
      })

      for (const entry of Object.entries(
        targetTemplateAttributesByDocumentPageId,
      )) {
        const [documentPageId, templateAttributes] = entry
        await bulkDeleteTemplateAttributes({
          documentPageId,
          // @ts-expect-error - bulk template attribates differ
          templateAttributes,
        })
      }
    }

    recordRedoEvent(overrides)
  }, [
    bulkDeleteTemplateAttributes,
    createTemplateAttribute,
    deleteTemplateAttribute,
    futureEvents,
    recordRedoEvent,
    updateTemplateAttribute,
  ])

  return { undo, redo }
}
